import { useCallback, useEffect, useMemo, useState } from "react";
import AccountSelecttionDropdown from "../directDebits/AccountSelecttionDropdown";
import { Matched } from "./matched&unMatched/matched&unMatched";
import type {
  DirectDebitAccount,
  DirectDebitMandate,
} from "@/app/types/accounts";
import { useDirectDebitAccount } from "../directDebits/apiHooks/useDirectDebitAccounts";
import { DrawerNoFooter } from "@/app/models/drawer/DrawerNoFooter";
import DirectDebitAccountList from "../directDebits/DirectDebitAccountList";
import DirectDebitDetails from "../directDebits/DirectDebitDetails";
import { useT } from "@/app/hooks/useT";
import AppDatePickerMini from "@/app/components/common/actions/inputs-b/AppDatePickerMini";
import dayjs from "dayjs";
import "../Reconciliation/reconciliation.css"; 
import "../Reconciliation/matched&unMatched/Transactionmatchingtable.css"
import { ICON_BASE_PATH } from "@/app/utils/config";

interface DrawerType {
  accountDrawer: boolean;
  mandateDetailDrawer: boolean;
}

interface DateFilter {
  dateFrom: Date | null;
  dateTo: Date | null;
}

const getDates = () => {
  const today = new Date();
  const currentDate = today;
  const pastDate = new Date(today);
  pastDate.setMonth(pastDate.getMonth() - 2);
  const twoMonthsBefore = pastDate;
  return { currentDate, twoMonthsBefore };
};

export const Reconciliation = () => {
  const t = useT();
  const [openDrawer, setOpenDrawer] = useState<DrawerType>({
    accountDrawer: false,
    mandateDetailDrawer: false,
  });
  const [selectedMandate, setSelectedMandate] = useState<DirectDebitMandate>();
  const [selectedAccount, setSelectedAccount] = useState<DirectDebitAccount>();
  const { directDebitAccounts } = useDirectDebitAccount({ page: 1 });
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    dateFrom: getDates().twoMonthsBefore,
    dateTo: getDates().currentDate,
  });

  const handleDrawer = () => {
    setOpenDrawer({ accountDrawer: false, mandateDetailDrawer: false });
  };

  const onSelectAccount = (account: DirectDebitAccount) => {
    setSelectedAccount(account);
    handleDrawer();
  };

  const handleFromDate = useCallback(
    (value: Date | null) => {
      setDateFilter((prev) => ({ ...prev, dateFrom: value }));
    },
    [setDateFilter],
  );

  const handleToDate = useCallback(
    (value: Date | null) => {
      setDateFilter((prev) => ({ ...prev, dateTo: value }));
    },
    [setDateFilter],
  );

  const maxFromDate = useMemo(() => {
    return dateFilter?.dateTo === null
      ? dayjs(new Date()).add(-1, "day").toDate()
      : dayjs(dateFilter?.dateTo).add(-1, "day").toDate();
  }, [dateFilter?.dateTo]);

  const minToDate = useMemo(() => {
    return dateFilter?.dateFrom === null
      ? undefined
      : dayjs(dateFilter?.dateFrom).add(1, "day").toDate();
  }, [dateFilter?.dateFrom]);

  const maxToDate = useMemo(() => {
    return dateFilter?.dateTo === null
      ? new Date()
      : dayjs(dateFilter?.dateTo).add(1, "day").toDate();
  }, [dateFilter?.dateTo]);

  useEffect(() => {
    if (directDebitAccounts.length > 0) {
      setSelectedAccount(directDebitAccounts[0]);
    }
  }, [directDebitAccounts]);

  return (
    <div className="gx-reconciliation-panel">
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <div className="gx-reconciliation-card">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-6">
                <div className="gx-reconciliation-date">
                  <div className="gx-reconciliation-lable">
                    <label className="gx-p12-md">{"From"}</label>
                    <AppDatePickerMini
                      placeholder={"Date From"}
                      onChange={handleFromDate}
                      value={dateFilter.dateFrom}
                      max={maxFromDate}
                    />
                  </div>
                  <div className="gx-reconciliation-lable">
                    <label className="gx-p12-md">{"To"}</label>
                    <AppDatePickerMini
                      placeholder={"Date To"}
                      onChange={handleToDate}
                      value={dateFilter.dateTo}
                      min={minToDate}
                      max={maxToDate}
                    />
                  </div>
                </div>
                <div className="gx-account-selection-view">
                  <AccountSelecttionDropdown
                    onClick={() =>
                      setOpenDrawer((prev: DrawerType) => ({
                        ...prev,
                        accountDrawer: !prev.accountDrawer,
                      }))
                    }
                    selectedAccount={selectedAccount || directDebitAccounts[0]}
                  />
                </div>
              </div>
              <div className="col-span-6 gx-reconciliation-child">
                <div className="gx-reconciliation-tab">
                  <button type="button">
                    <img src={`${ICON_BASE_PATH}runnow.svg`} alt="" />
                    Run Now
                  </button>
                  <button type="button">
                    <img src={`${ICON_BASE_PATH}export.svg`} alt="" />
                    Export
                  </button>
                  <button type="button">
                    <img src={`${ICON_BASE_PATH}markall.svg`} alt="" />
                    Mark All Reviewed
                  </button>
                </div>
                <div className="gx-reconciliation-time">
                  <p>
                    Last Run : <span>23 Jul 2026, 10.30 AM</span>
                  </p>

                  <p>
                    Auto-matched : <span>1,520</span>
                  </p>
                  <p>
                    Pending Review : <span>20</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-12">
          <div className="gx-reconciliation-card">
            <Matched />
          </div>
        </div>
      </div>
      {(openDrawer.accountDrawer || openDrawer.mandateDetailDrawer) && (
        <DrawerNoFooter
          title={openDrawer.accountDrawer ? t("casa") : "Direct"}
          subtitle={openDrawer.accountDrawer ? t("list") : "Debit Detail"}
          openDrawer={
            openDrawer.accountDrawer || openDrawer.mandateDetailDrawer
          }
          onClose={handleDrawer}
        >
          {openDrawer.accountDrawer ? (
            <DirectDebitAccountList
              selectedAccountId={selectedAccount?.cbsAccountId || ""}
              onSelect={onSelectAccount}
              placeholder={"Search by Account Name / Account Number"}
            />
          ) : (
            openDrawer.mandateDetailDrawer &&
            selectedAccount &&
            selectedMandate && (
              <DirectDebitDetails
                directDebitId={selectedMandate?.directDebitId}
              />
            )
          )}
        </DrawerNoFooter>
      )}
    </div>
  );
};
